import { type CSSProperties } from 'react';

import type { SxProps, Theme } from '@mui/material';

const TABLE_SCROLLBAR_SIZE = 8;
const TABLE_VERTICAL_SCROLLBAR_OFFSET_X = 12;
const TABLE_VERTICAL_SCROLLBAR_OFFSET_Y = 8;
const TABLE_HORIZONTAL_SCROLLBAR_OFFSET_X = 16;
const TABLE_HORIZONTAL_SCROLLBAR_OFFSET_Y = 12;
const TABLE_RESIZE_HANDLE_SAFE_SPACE = 8;

const TABLE_VERTICAL_SCROLLBAR_SLOT_WIDTH =
  TABLE_SCROLLBAR_SIZE + TABLE_VERTICAL_SCROLLBAR_OFFSET_X * 2;
const TABLE_HORIZONTAL_SCROLLBAR_SLOT_HEIGHT =
  TABLE_SCROLLBAR_SIZE + TABLE_HORIZONTAL_SCROLLBAR_OFFSET_Y * 2;

const getTableScrollbarTrackColor = (theme: Theme, isHover: boolean) =>
  isHover ? theme.palette.accent_25 : theme.palette.backgroundColor;

const getTableScrollbarThumbColor = (theme: Theme, isHover: boolean) =>
  isHover ? theme.palette.accent : theme.palette.pale_accent;

const getTableHeadContentJustifyContent = (align?: string) => {
  if (align === 'left') {
    return 'flex-start';
  }

  if (align === 'right') {
    return 'flex-end';
  }

  return 'center';
};

export const resizableTableScrollbarTrackClassName = 'ResizableTable-scrollbarTrack';
export const resizableTableScrollbarThumbClassName = 'ResizableTable-scrollbarThumb';

const hiddenNativeScrollbarSx: SxProps<Theme> = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    height: 0,
  },
};

export const resizableTablePaperSx = (
  fillThePage: boolean,
  additionalSx?: SxProps<Theme>
): SxProps<Theme> => ({
  backgroundColor: (theme: Theme) => theme.palette.white_100,
  borderRadius: 4,
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  minWidth: 0,
  '--Paper-shadow': 'none !important',
  ...(fillThePage && {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  }),
  ...(additionalSx || {}),
});

export const resizableTableScrollLayoutSx = (fillThePage: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  backgroundColor: (theme: Theme) => theme.palette.white_100,
  ...(fillThePage && {
    flex: 1,
    minHeight: 0,
  }),
});

export const resizableTableScrollContentRowSx = (
  fillThePage: boolean,
  headerHeight: number
): SxProps<Theme> => ({
  position: 'relative',
  display: 'flex',
  width: '100%',
  minWidth: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    height: `${headerHeight}px`,
    backgroundColor: (theme: Theme) => theme.palette.pale_accent,
    pointerEvents: 'none',
    zIndex: 0,
  },
  ...(fillThePage && {
    flex: 1,
    minHeight: 0,
  }),
});

export const resizableTableViewportSx = (fillThePage: boolean): SxProps<Theme> => ({
  position: 'relative',
  width: '100%',
  minWidth: 0,
  ...(fillThePage && {
    flex: 1,
    minHeight: 0,
    display: 'flex',
  }),
});

export const resizableTableContainerSx = (
  fillThePage: boolean,
  maxHeight?: CSSProperties['maxHeight'],
  additionalSx?: SxProps<Theme>
): SxProps<Theme> => [
  {
    overflow: 'auto',
    position: 'relative',
    width: '100%',
    minWidth: 0,
    zIndex: 1,
    ...(fillThePage && {
      height: '100%',
      flex: 1,
      minHeight: 0,
    }),
    ...(maxHeight !== undefined && { maxHeight }),
  },
  hiddenNativeScrollbarSx,
  ...(Array.isArray(additionalSx) ? additionalSx : additionalSx ? [additionalSx] : []),
];

export const resizableTableHeadBackdropSx = (height: number): SxProps<Theme> => ({
  position: 'sticky',
  top: 0,
  left: 0,
  width: '100%',
  minWidth: '100%',
  height,
  mb: height > 0 ? `-${height}px` : 0,
  backgroundColor: (theme: Theme) => theme.palette.pale_accent,
  pointerEvents: 'none',
  zIndex: 3,
});

export const resizableTableVerticalScrollbarColumnSx = (
  fillThePage: boolean,
  headerHeight: number
): SxProps<Theme> => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  // Keep the viewport width stable when filtering replaces scrollable rows with skeletons.
  width: TABLE_VERTICAL_SCROLLBAR_SLOT_WIDTH,
  minHeight: 0,
  pt: `${headerHeight}px`,
  boxSizing: 'border-box',
  zIndex: 1,
  ...(fillThePage && {
    height: '100%',
  }),
});

export const resizableTableScrollbarSlotSx = (axis: 'vertical' | 'horizontal'): SxProps<Theme> => ({
  display: 'flex',
  flexShrink: 0,
  backgroundColor: (theme: Theme) => theme.palette.white_100,
  userSelect: 'none',
  touchAction: 'none',
  ...(axis === 'vertical'
    ? {
        width: TABLE_VERTICAL_SCROLLBAR_SLOT_WIDTH,
        px: `${TABLE_VERTICAL_SCROLLBAR_OFFSET_X}px`,
        py: `${TABLE_VERTICAL_SCROLLBAR_OFFSET_Y}px`,
        flex: 1,
        minHeight: 0,
      }
    : {
        height: TABLE_HORIZONTAL_SCROLLBAR_SLOT_HEIGHT,
        px: `${TABLE_HORIZONTAL_SCROLLBAR_OFFSET_X}px`,
        py: `${TABLE_HORIZONTAL_SCROLLBAR_OFFSET_Y}px`,
        flex: 1,
        minWidth: 0,
      }),
  [`&:hover .${resizableTableScrollbarTrackClassName}`]: {
    backgroundColor: (theme: Theme) => getTableScrollbarTrackColor(theme, true),
  },
  [`&:hover .${resizableTableScrollbarThumbClassName}`]: {
    backgroundColor: (theme: Theme) => getTableScrollbarThumbColor(theme, true),
  },
});

export const resizableTableScrollbarTrackSx = (
  axis: 'vertical' | 'horizontal'
): SxProps<Theme> => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 1,
  backgroundColor: (theme: Theme) => getTableScrollbarTrackColor(theme, false),
  transition: 'background-color 120ms ease',
  [`&:has(.${resizableTableScrollbarThumbClassName}:active)`]: {
    backgroundColor: (theme: Theme) => getTableScrollbarTrackColor(theme, true),
  },
  ...(axis === 'vertical'
    ? {
        width: TABLE_SCROLLBAR_SIZE,
        height: '100%',
      }
    : {
        width: '100%',
        height: TABLE_SCROLLBAR_SIZE,
      }),
});

export const resizableTableScrollbarThumbSx = (
  axis: 'vertical' | 'horizontal'
): SxProps<Theme> => ({
  display: 'none',
  position: 'absolute',
  borderRadius: 1,
  backgroundColor: (theme: Theme) => getTableScrollbarThumbColor(theme, false),
  transition: 'background-color 120ms ease',
  '&:active': {
    backgroundColor: (theme: Theme) => getTableScrollbarThumbColor(theme, true),
  },
  ...(axis === 'vertical'
    ? {
        top: 0,
        left: 0,
        width: TABLE_SCROLLBAR_SIZE,
        height: 0,
        transform: 'translateY(0)',
      }
    : {
        top: 0,
        left: 0,
        width: 0,
        height: TABLE_SCROLLBAR_SIZE,
        transform: 'translateX(0)',
      }),
});

export const resizableTableScrollbarFooterSx = (): SxProps<Theme> => ({
  display: 'flex',
  width: '100%',
  minWidth: 0,
  backgroundColor: (theme: Theme) => theme.palette.white_100,
});

export const resizableTableScrollbarCornerSx = (): SxProps<Theme> => ({
  width: TABLE_VERTICAL_SCROLLBAR_SLOT_WIDTH,
  height: TABLE_HORIZONTAL_SCROLLBAR_SLOT_HEIGHT,
  flexShrink: 0,
  backgroundColor: (theme: Theme) => theme.palette.white_100,
});

export const resizableTableRootSx = (
  totalWidth: number,
  additionalSx?: SxProps<Theme>
): SxProps<Theme> => ({
  width: totalWidth,
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
  ...(additionalSx || {}),
});

export const resizableTableHeadSx = {
  background: 'transparent',
};

export const resizableTableHeadRowSx = {
  th: {
    px: 4,
  },
  'th:first-of-type': {
    pl: 4,
    pr: 5,
  },
  'th:last-of-type': {
    pr: 4,
    pl: 5,
  },
};

export const resizableTableHeadCellSx = (theme: Theme): SxProps<Theme> => ({
  ...theme.typography.body_14_20,
  color: theme.palette.dark,
  px: 4,
  py: 2,
  border: 'none',
  backgroundColor: theme.palette.pale_accent,
  position: 'relative',
  overflow: 'hidden',
  userSelect: 'none',
  '&.MuiTableCell-stickyHeader': {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.pale_accent,
    zIndex: 5,
  },
});

export const resizableTableHeadContentSx = (
  hasResizeHandle: boolean,
  align?: string
): SxProps<Theme> => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: getTableHeadContentJustifyContent(align),
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  boxSizing: 'border-box',
  pr: hasResizeHandle ? `${TABLE_RESIZE_HANDLE_SAFE_SPACE}px` : 0,
  columnGap: '0.25em',
  overflow: 'hidden',
  textAlign: 'inherit',
  lineHeight: 'inherit',
});

export const resizableTableHeadWordSx: SxProps<Theme> = {
  display: 'block',
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 'inherit',
};

export const resizableTableBodySx = {
  background: 'transparent',
};

export const resizableTableRowSx = (theme: Theme, isClickable: boolean): SxProps<Theme> => ({
  ...(isClickable && {
    cursor: 'pointer',
  }),
  '&:hover': {
    background: theme.palette.accent_25,
  },
  td: {
    px: 4,
  },
  'td:first-of-type': {
    pl: 4,
    pr: 5,
  },
  'td:last-of-type': {
    pr: 4,
    pl: 5,
  },
});

/** Stretches a cell's link across its row while keeping table markup and cell layout intact. */
export const resizableTableLinkedRowSx: SxProps<Theme> = {
  position: 'relative',
  '&:focus-within': {
    backgroundColor: (theme: Theme) => theme.palette.accent_25,
  },
  // Raise control roots without overriding the absolute input that covers a MUI checkbox.
  '& td :is(.MuiButtonBase-root, button, a, select, textarea, [role="button"], [role="checkbox"], label):not(.ResizableTable-rowLink)':
    {
      position: 'relative',
      zIndex: 2,
    },
};

export const resizableTableRowLinkSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  outline: 'none',
};

export const resizableTableCellSx = (theme: Theme): SxProps<Theme> => ({
  ...theme.typography.body_14_24,
  py: 2,
  px: 4,
  color: theme.palette.dark,
  borderBottom: 'none',
  overflow: 'hidden',
});

export const resizableTableResizeHandleSx = (isResizing: boolean): SxProps<Theme> => ({
  position: 'absolute',
  top: '50%',
  right: 0,
  width: 14,
  height: '100%',
  cursor: 'col-resize',
  touchAction: 'none',
  userSelect: 'none',
  transform: 'translateY(-50%)',
  zIndex: 3,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    right: 6,
    width: 2,
    height: 16,
    borderRadius: 1,
    backgroundColor: (theme: Theme) => (isResizing ? theme.palette.accent : theme.palette.dark),
    opacity: isResizing ? 1 : 0.65,
    transform: 'translateY(-50%)',
    transition: 'opacity 120ms ease, background-color 120ms ease, height 120ms ease',
  },
  '&:hover::after': {
    opacity: 1,
    height: 20,
  },
});

export const resizableTableDividerCellSx = {
  border: 'none',
  p: (theme: Theme) => theme.spacing(1),
};

export const resizableTableDividerSx = {
  m: (theme: Theme) => `0 ${theme.spacing(4)}`,
  height: '1px',
  backgroundColor: (theme: Theme) => theme.palette.pale_accent,
};

export const resizableTableFullWidthCellSx = {
  p: 0,
  borderBottom: 'none',
};

export const resizableTablePinnedSubRowContentSx = (containerWidth: number): SxProps<Theme> => ({
  position: 'sticky',
  left: 0,
  width: containerWidth > 0 ? `${containerWidth}px` : '100%',
  minWidth: containerWidth > 0 ? `${containerWidth}px` : '100%',
  maxWidth: '100%',
  backgroundColor: (theme: Theme) => theme.palette.white_100,
  boxSizing: 'border-box',
  zIndex: 2,
});

export const resizableTableLoaderCellSx = {
  borderBottom: 'none',
  py: 4,
};

export const resizableTableEmptyStateSx = (
  fillThePage: boolean,
  headerHeight: number
): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  color: (theme: Theme) => theme.palette.light_dark,
  ...(fillThePage
    ? {
        position: 'absolute',
        top: `${headerHeight}px`,
        right: 0,
        bottom: 0,
        left: 0,
        minHeight: 0,
        zIndex: 2,
      }
    : {
        position: 'sticky',
        left: 0,
        width: '100%',
        minHeight: 120,
      }),
});
